import { Avatar, Box, IconButton, Typography, styled, useTheme } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import VideoCameraFrontIcon from '@mui/icons-material/VideoCameraFront';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { buildBackendUrl } from '../../../config/API';
import { useAuthContext } from '../../auth/hooks/useAuthContext';
import { getInitialForAvatar } from './../helpers/helperMethods/getInitials';

const Card = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    minWidth: 0,
    overflow: 'hidden',
}));

const Media = styled(Box)({
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    minHeight: 0,
    overflow: 'hidden',
    position: 'relative',
    width: '100%',
});

const Details = styled(Box)(({ theme }) => ({
    alignItems: 'center',
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    flex: '0 0 48px',
    gap: 4,
    justifyContent: 'space-between',
    minWidth: 0,
    padding: '0 8px',
}));

const StudentMeetingCard = ({
    cardDimensions,
    attendee,
    toturStream,
    handleStreamTutorAudio,
    handleStreamTutorCam,
    handleStreamStudentAudio,
    handleStreamStudentCam,
}) => {
    const auth = useAuthContext();
    const user = auth?.user || JSON.parse(localStorage.getItem('user') || 'null');
    const theme = useTheme();
    const videoRef = useRef();
    const [hasLiveVideo, setHasLiveVideo] = useState(false);
    const mediaStream = useMemo(
        () => attendee.user_type !== 0 && toturStream ? toturStream : attendee.stream,
        [attendee.stream, attendee.user_type, toturStream],
    );

    useEffect(() => {
        const video = videoRef.current;
        const videoTrack = mediaStream?.getVideoTracks?.()[0];
        if (!video || !mediaStream || !videoTrack) {
            if (video) video.srcObject = null;
            setHasLiveVideo(false);
            return undefined;
        }

        const showVideo = () => setHasLiveVideo(videoTrack.readyState === 'live');
        const hideVideo = () => setHasLiveVideo(false);
        video.srcObject = mediaStream;
        video.defaultMuted = true;
        video.muted = true;
        showVideo();
        video.play().catch(() => {});
        videoTrack.addEventListener('unmute', showVideo);
        videoTrack.addEventListener('ended', hideVideo);
        return () => {
            videoTrack.removeEventListener('unmute', showVideo);
            videoTrack.removeEventListener('ended', hideVideo);
            if (video.srcObject === mediaStream) video.srcObject = null;
        };
    }, [mediaStream]);

    const handleAudio = () => {
        if (attendee.user_type !== 0) handleStreamTutorAudio?.();
        else handleStreamStudentAudio?.(attendee);
    };

    const handleCamera = () => {
        if (attendee.user_type !== 0) handleStreamTutorCam?.();
        else handleStreamStudentCam?.(attendee);
    };

    const isCurrentUser = String(user?.id) === String(attendee.user_id);

    return (
        <Card
            data-testid="meeting-card"
            sx={{ width: cardDimensions.cardWidth, height: cardDimensions.cardHeight }}
        >
            <Media>
                <Avatar
                    sx={{
                        display: hasLiveVideo ? 'none' : 'flex',
                        height: Math.min(cardDimensions.cardWidth * 0.28, 128),
                        width: Math.min(cardDimensions.cardWidth * 0.28, 128),
                    }}
                    alt={attendee?.user_name}
                    src={buildBackendUrl(attendee?.user_image)}
                >
                    {getInitialForAvatar(attendee?.user_name)}
                </Avatar>
                <video
                    ref={videoRef}
                    autoPlay
                    muted
                    playsInline
                    data-testid="meeting-video"
                    style={{
                        display: hasLiveVideo ? 'block' : 'none',
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                    }}
                />
            </Media>
            <Details>
                <Typography
                    noWrap
                    title={attendee?.user_name}
                    sx={{
                        color: attendee?.user_type !== 0 ? theme.palette.secondary.main : theme.palette.text.primary,
                        direction: theme.direction,
                        fontSize: '0.9rem',
                        minWidth: 0,
                    }}
                >
                    {attendee?.user_name}
                </Typography>
                {isCurrentUser && (
                    <Box sx={{ display: 'flex', flexShrink: 0 }}>
                        <IconButton size="small" color="secondary" onClick={handleAudio} aria-label="Toggle meeting microphone">
                            {attendee.micEnabled ? <MicOffIcon /> : <MicIcon />}
                        </IconButton>
                        <IconButton size="small" color="secondary" onClick={handleCamera} aria-label="Toggle meeting camera">
                            {attendee.camEnabled ? <VideocamOffIcon /> : <VideoCameraFrontIcon />}
                        </IconButton>
                    </Box>
                )}
            </Details>
        </Card>
    );
};

export default StudentMeetingCard;
